import React, { useEffect, useState, useRef } from 'react';
import {
	XThemeContainer,
} from '../../styles/GlobalThemeStyle';
import { Row, Col, Grid, Form, Typography, Tag } from 'antd';
import Header from '../../components/Header/Header';
import HeaderFile from '../../components/Header/HeaderFile';
import { PhoneOutlined, StopOutlined, SoundOutlined } from '@ant-design/icons';
import { useConversation } from '@elevenlabs/react';
import {
	XTheme2InnerCard,
	XTheme2Para1,
} from '../Dashboard/DashboardStyleCQ';
import {
	XTheme2Button,
	XTheme2InputText,
	XTheme2MainTitle,
} from '../../styles/GlobalThemeStyleCQ';
import config from '../../config';

const CallInsurance = (props) => {
	const [form] = Form.useForm();
	const [transcript, setTranscript] = useState([]);
	const [claimStatus, setClaimStatus] = useState('Claim Submitted');
	const [error, setError] = useState(null);
	const [retryCount, setRetryCount] = useState(0);
	const [micPermission, setMicPermission] = useState(false);
	const [isCallActive, setIsCallActive] = useState(false);
	const { Title, Text } = Typography;
	const transcriptEndRef = useRef(null);
	const conversationIdRef = useRef(null);

	const maxRetries = 3;

	const conversation = useConversation({
		onConnect: (data) => {
			setError(null);
			setIsCallActive(true);
			conversationIdRef.current = data.conversationId;
		},
		onDisconnect: (data) => {
			handleCallEnd();
			const convIdToUse = conversationIdRef.current;
			if (convIdToUse) {
				fetchConversationAnalysis(convIdToUse);
			}
		},
		onMessage: (message) => {
			handleConversationMessage(message);
		},
		onAudio: (audio) => {
			if (audio.byteLength > 0) {
				const audioBlob = new Blob([audio], { type: 'audio/mpeg' });
				const audioUrl = URL.createObjectURL(audioBlob);
				const audioPlayer = new Audio(audioUrl);
				audioPlayer.play().catch(e => {});
			}
		},
		onError: (error) => {
			setError(`Conversation error: ${error.message || 'Unknown error'}`);
		},
		onAnalysis: (analysis) => {
			if (analysis && analysis.data_collection_results && analysis.data_collection_results.claim_status) {
				const claimStatusData = analysis.data_collection_results.claim_status;
				if (claimStatusData.value) {
					setClaimStatus(claimStatusData.value);
				}
			}
		},
		onDebug: (debug) => {}
	});

	// Handle messages from ElevenLabs conversation
	const handleConversationMessage = (message) => {
		if (message.source === 'user') {
			setTranscript(prev => [...prev, { type: 'user', message: message.message, timestamp: new Date() }]);
		} else if (message.source === 'ai') {
			const agentMessage = message.message || '';
			setTranscript(prev => [...prev, { type: 'agent', message: agentMessage, timestamp: new Date() }]);
		}
		setTimeout(() => scrollToBottom(), 100);
	};

	// Auto-scroll to bottom of transcript
	const scrollToBottom = () => {
		if (transcriptEndRef.current) {
			transcriptEndRef.current.scrollIntoView({ behavior: 'smooth' });
		}
	};

	// Fetch conversation analysis from ElevenLabs REST API
	const fetchConversationAnalysis = async (convId) => {
		try {
			setTimeout(async () => {
				try {
					const token = localStorage.getItem('accessToken');
					const response = await fetch(`${config.api.base_url}/call-insurance/conversation-analysis/${convId}`, {
						method: 'GET',
						headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
					});
					
					if (response.ok) {
						const analysisData = await response.json();
						if (analysisData.success && analysisData.data?.analysis?.data_collection_results?.claim_status?.value) {
							const claimStatusValue = analysisData.data.analysis.data_collection_results.claim_status.value;
							setClaimStatus(claimStatusValue);
						}
					}
				} catch (apiError) {
				}
			}, 3000);
		} catch (error) {
		}
	};

	const handleRetry = () => {
		if (retryCount < maxRetries) {
			setRetryCount(prev => prev + 1);
			setError(null);
			setMicPermission(false);
			handleStartCall();
		}
	};

	const handleStartCall = async () => {
		try {
			const formValues = await form.validateFields();
			setError(null);
			setTranscript([]);
			setClaimStatus('Claim Submitted');
			conversationIdRef.current = null;
			
			if (!micPermission) {
				try {
					await navigator.mediaDevices.getUserMedia({ audio: true });
					setMicPermission(true);
				} catch (error) {
					setError('Microphone permission is required. Please allow microphone access and try again.');
					return;
				}
			}

			const token = localStorage.getItem('accessToken');
			const response = await fetch(`${config.api.base_url}/call-insurance/generate-signed-url`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
				body: JSON.stringify({ dynamicVariables: formValues })
			});

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(errorData.message || 'Failed to get signed URL');
			}

			const { data } = await response.json();
			const { signedUrl, agentId } = data;

			await conversation.startSession({
				signedUrl,
				agentId,
				dynamicVariables: formValues,
			});
			
		} catch (error) {
			let errorMessage = 'Failed to start call';
			if (error.message?.includes('microphone') || error.message?.includes('permission')) {
				errorMessage = 'Microphone permission required. Please allow access and try again.';
			} else if (error.message) {
				errorMessage = error.message;
			}
			setError(errorMessage);
		}
	};

	const handleEndCall = () => {
		try {
			conversation.endSession();
		} catch (error) {
		}
	};

	const handleCallEnd = () => {
		setIsCallActive(false);
	};

	const { useBreakpoint } = Grid;
	const screens = useBreakpoint();
	
	return (
		<>
			<style>{`
				.recording-indicator {
					width: 8px;
					height: 8px;
					background-color: #52c41a;
					border-radius: 50%;
					animation: pulse 1.5s infinite;
				}
				.transcript-container {
					scroll-behavior: smooth;
				}
				.transcript-container::-webkit-scrollbar { width: 6px; }
				.transcript-container::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 3px; }
				.transcript-container::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 3px; }
			`}</style>
			<Col lg={24} xl={24} xs={0} md={0}>
				<Header headerTag="Call Insurance" />
			</Col>
			<Col lg={0} xl={0} xs={24} md={24}>
				<HeaderFile />
			</Col>
			<XThemeContainer fluid>
				<Row gutter={[15, 15]} className="align-items-center mb-4">
					<Col xs={24} md={12} className="text-center text-md-start">
						<XTheme2MainTitle className="mb-2">Call Insurance</XTheme2MainTitle>
						<XTheme2Para1>Agent Maxx will call the insurance company to get the latest information about the claim.</XTheme2Para1>
					</Col>
				</Row>

				<Row gutter={[24, 24]}>
					<Col xs={24} lg={12}>
						<XTheme2InnerCard 
							title={
								<div className="d-flex justify-content-between align-items-center">
									<span>Patient Information</span>
									<Tag color="blue">Claim Status: {claimStatus}</Tag>
								</div>
							}
							headStyle={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}
						>
							<Form form={form} layout="vertical" requiredMark={false}>
								<Form.Item label="Patient Name" name="patient_name" rules={[{ required: true }]} initialValue="Pluto">
									<XTheme2InputText placeholder="Enter patient name" />
								</Form.Item>
								<Form.Item label="Date of Birth" name="patient_dob" rules={[{ required: true }]} initialValue="08/05/1990">
									<XTheme2InputText placeholder="MM/DD/YYYY" />
								</Form.Item>
								<Form.Item label="Policy Number" name="patient_policy_number" rules={[{ required: true }]} initialValue="5216">
									<XTheme2InputText placeholder="Enter policy number" />
								</Form.Item>
								<Form.Item label="Claim ID" name="claim_id" rules={[{ required: true }]} initialValue="8569">
									<XTheme2InputText placeholder="Enter claim ID" />
								</Form.Item>
								<Form.Item label="Date of Service" name="date_of_service" rules={[{ required: true }]} initialValue="06/05/2025">
									<XTheme2InputText placeholder="MM/DD/YYYY" />
								</Form.Item>
								<Form.Item label="Provider" name="provider" rules={[{ required: true }]} initialValue="Nano Health">
									<XTheme2InputText placeholder="Enter provider name" />
								</Form.Item>
								<div className="text-center mt-4">
									{conversation.status !== 'connected' && (
										<XTheme2Button type="primary" size="large" icon={<PhoneOutlined />} onClick={handleStartCall} loading={conversation.status === 'connecting'} style={{ minWidth: '150px' }}>
											{conversation.status === 'connecting' ? 'Connecting...' : 'Start Call'}
										</XTheme2Button>
									)}
									{error && (
										<div className="mt-3 text-center">
											<div className="alert alert-danger">{error}</div>
											{retryCount < maxRetries && (
												<XTheme2Button type="default" size="small" onClick={handleRetry}>
													Retry ({retryCount}/{maxRetries})
												</XTheme2Button>
											)}
										</div>
									)}
									{conversation.status === 'connected' && (
										<XTheme2Button type="primary" danger size="large" icon={<StopOutlined />} onClick={handleEndCall} style={{ minWidth: '150px' }}>
											End Call
										</XTheme2Button>
									)}
								</div>
							</Form>
						</XTheme2InnerCard>
					</Col>
					<Col xs={24} lg={12}>
						<XTheme2InnerCard
							title={(
								<div className="d-flex align-items-center">
									<SoundOutlined className="me-2" />
									Call Transcript
									{isCallActive && <span className="ms-2 text-success">● Live</span>}
									{conversation.isSpeaking && <span className="ms-2 text-warning">🎤 Speaking...</span>}
								</div>
							)}
							headStyle={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}
							bodyStyle={{ height: '550px', overflowY: 'auto', padding: '0' }}
						>
							{transcript.length === 0 ? (
								<div className="text-center text-muted py-5">
									<SoundOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
									<p>Start a call to see the conversation transcript here</p>
								</div>
							) : (
								<div className="transcript-container" style={{ maxHeight: '510px', overflowY: 'auto', padding: '16px' }}>
									{transcript.map((entry, index) => (
										<div key={index} className={`mb-3 p-3 rounded ${entry.type === 'user' ? 'bg-light' : 'bg-primary text-white'}`}>
											<div className="d-flex justify-content-between align-items-start">
												<strong>{entry.type === 'user' ? 'You' : 'Agent Maxx'}:</strong>
												<small className={entry.type === 'user' ? 'text-muted' : 'text-light'}>{entry.timestamp.toLocaleTimeString()}</small>
											</div>
											<p className="mb-0 mt-2">{entry.message}</p>
										</div>
									))}
									<div ref={transcriptEndRef} />
								</div>
							)}
						</XTheme2InnerCard>
					</Col>
				</Row>
			</XThemeContainer>
		</>
	);
};

export default CallInsurance;
